// Form inputs for the Vision display formats a FORM honours (``format`` on
// the column — see utils.visionFieldFormat). Grid cells format these on
// display; here they shape the editor itself.
//
//   (any number) → NumericInput: a text box with a decimal keypad and no
//                  spinner — a mouse wheel over it never changes the value.
//   [Percentage] → a numeric text box with a "%" button that opens Vision's
//                  0–100 dial. The stored value is the number as typed (12.5
//                  means 12.5 %) — never rescaled.
//   [Email]      → an email box with Vision's mail icon (mailto:) once valid.
//   [Phone]      → a phone box with a dial icon (tel:).
//   [WebUrl]     → a URL box with an open-in-new-tab icon.
//
// The icons stay usable on a read-only field (Vision's link works in view
// mode) whenever there is a value to act on.

import { useEffect, useState } from 'react'
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ExternalLink, Mail, Phone } from 'lucide-react'

// Partial numbers the user may be in the middle of typing ("-", "12.").
const NUMERIC_DRAFT = /^-?\d*(\.\d*)?$/

function toDraft(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : ''
  return String(v)
}

type NumericInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: unknown
  /** The number once the text is one; '' when cleared. */
  onChange: (v: number | '') => void
}

/** A number typed as TEXT: `inputMode="decimal"` brings up the numeric
 *  keypad, there is no spinner, and the mouse wheel scrolls the page instead
 *  of nudging the value. Keeps the text being typed ("12." is a step towards
 *  12.5) and hands the form a number; a value set from outside (item_change,
 *  record load) replaces the text when it is a different number. A stored
 *  value that is not a number (a token) is shown as-is until edited. */
export function NumericInput({ value, onChange, ...rest }: NumericInputProps) {
  const [draft, setDraft] = useState(() => toDraft(value))
  useEffect(() => {
    const ext = toDraft(value)
    setDraft(prev => {
      if (prev === ext) return prev
      if (ext !== '' && prev !== '' && Number(prev) === Number(ext)) return prev
      return ext
    })
  }, [value])
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={draft}
      onChange={e => {
        const t = e.target.value.replace(/,/g, '').trim()
        if (!NUMERIC_DRAFT.test(t)) return
        setDraft(t)
        if (t === '') { onChange(''); return }
        const n = Number(t)
        if (Number.isFinite(n) && t !== '-' && t !== '.' && t !== '-.') onChange(n)
      }}
    />
  )
}

// A small icon button drawn inside the right edge of a field.
function FieldIconButton({ title, onClick, enabled, color, children }: {
  title: string
  onClick: () => void
  enabled: boolean
  color?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      tabIndex={-1}
      disabled={!enabled}
      onClick={e => { e.preventDefault(); e.stopPropagation(); if (enabled) onClick() }}
      style={{
        position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 3, border: 'none', borderRadius: 3, background: 'transparent',
        color: enabled ? (color || 'var(--color-accent)') : 'var(--color-text-secondary)',
        opacity: enabled ? 1 : 0.5,
        cursor: enabled ? 'pointer' : 'default',
      }}
    >{children}</button>
  )
}

export function PercentField({
  value, onChange, placeholder, disabled, style, fieldKey, label,
}: {
  value: unknown
  onChange: (v: number | string) => void
  placeholder?: string
  disabled?: boolean
  style?: CSSProperties
  fieldKey: string
  /** Field caption — the dial's title. */
  label?: string
}) {
  const [dialOpen, setDialOpen] = useState(false)
  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      <NumericInput
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={v => onChange(v)}
        style={{ ...style, flex: 1, minWidth: 0, paddingRight: 32, textAlign: 'right' }}
        data-livepreview-field={fieldKey}
      />
      <FieldIconButton
        title={disabled ? 'Show percentage dial' : 'Set with the percentage dial'}
        enabled
        color="var(--color-text-secondary)"
        onClick={() => setDialOpen(true)}
      >
        <span style={{ fontSize: '0.9125rem', fontWeight: 600, lineHeight: 1, minWidth: 14 }}>%</span>
      </FieldIconButton>
      {dialOpen && (
        <PercentDial
          title={label || 'Percentage'}
          value={value}
          readOnly={!!disabled}
          onCancel={() => setDialOpen(false)}
          onDone={n => { setDialOpen(false); onChange(n) }}
        />
      )}
    </div>
  )
}

// Vision's [Percentage] knob: a 0–100 dial (step 0.1) titled with the field
// caption, opened on the current value (0 when empty); Done writes it back.
function PercentDial({ title, value, readOnly, onCancel, onDone }: {
  title: string
  value: unknown
  readOnly: boolean
  onCancel: () => void
  onDone: (n: number) => void
}) {
  const start = (() => {
    const n = Number(value)
    if (value == null || value === '' || !Number.isFinite(n)) return 0
    return Math.min(100, Math.max(0, n))
  })()
  const [n, setN] = useState<number>(start)
  const [text, setText] = useState<string>(String(start))
  const set = (v: number) => {
    const c = Math.round(Math.min(100, Math.max(0, v)) * 10) / 10
    setN(c)
    setText(String(c))
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12000,
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-label={title}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(360px, 94vw)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
          fontWeight: 600, fontSize: '0.9625rem',
        }}>{title}</div>
        <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 120, height: 120, borderRadius: '50%',
            background: `conic-gradient(var(--color-accent, #4c8dff) ${n * 3.6}deg, var(--color-border, #e5e5e5) 0deg)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 92, height: 92, borderRadius: '50%', background: 'var(--color-surface, #fff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.3625rem', fontWeight: 600,
            }}>{n}%</div>
          </div>
          <input
            type="range"
            min={0} max={100} step={0.1}
            value={n}
            disabled={readOnly}
            aria-label={`${title} (0–100)`}
            onChange={e => set(Number(e.target.value))}
            style={{ width: '100%', accentColor: 'var(--color-accent)' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="text"
              inputMode="decimal"
              value={text}
              disabled={readOnly}
              aria-label={title}
              onChange={e => {
                const t = e.target.value.replace(/,/g, '').trim()
                if (!/^\d*(\.\d?)?$/.test(t)) return
                setText(t)
                const v = Number(t)
                if (t !== '' && t !== '.' && Number.isFinite(v)) setN(Math.min(100, v))
              }}
              onBlur={() => set(n)}
              style={{
                width: 80, padding: '6px 8px', textAlign: 'right', fontSize: '0.9225rem',
                borderRadius: 6, border: '1px solid var(--color-border, #ccc)',
                backgroundColor: 'var(--color-bg, #fff)', color: 'var(--color-text, #111)',
              }}
            />
            <span style={{ fontSize: '0.9225rem', color: 'var(--color-text-secondary)' }}>%</span>
          </div>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text, #111)' }}
          >{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button
              type="button"
              onClick={() => onDone(Math.round(Math.min(100, Math.max(0, n)) * 10) / 10)}
              style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff', cursor: 'pointer' }}
            >Done</button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

// Deliberately loose (one @, a dotted domain, no spaces) — it marks a
// probable typo, it never blocks a save.
export function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())
}

/** Loose: phone punctuation only, and at least five digits. */
export function looksLikePhone(v: string): boolean {
  const t = v.trim()
  return /^\+?[\d\s().\-/]+$/.test(t) && (t.match(/\d/g) || []).length >= 5
}

/** The address a [WebUrl] value opens — a bare host gets https://. */
export function webUrlHref(v: string): string | null {
  const t = v.trim()
  if (!t || /\s/.test(t)) return null
  if (/^https?:\/\//i.test(t)) return t
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return null  // javascript:, data:, …
  return /^[^/]+\.[^/]{2,}/.test(t) ? `https://${t}` : null
}

interface LinkFieldProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  style?: CSSProperties
  fieldKey: string
}

// A text box with one action icon inside its right edge — the shape all
// three Vision link formats share.
function LinkField({
  value, onChange, placeholder, maxLength, disabled, style, fieldKey,
  inputType, inputMode, autoComplete, valid, icon, iconTitle, onIcon,
}: LinkFieldProps & {
  inputType: string
  inputMode: InputHTMLAttributes<HTMLInputElement>['inputMode']
  autoComplete: string
  valid: boolean
  icon: ReactNode
  iconTitle: string
  onIcon: () => void
}) {
  const filled = value.trim() !== ''
  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      <input
        type={inputType}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        aria-invalid={filled && !valid ? true : undefined}
        onChange={e => onChange(e.target.value)}
        style={{ ...style, flex: 1, minWidth: 0, paddingRight: 30 }}
        data-livepreview-field={fieldKey}
      />
      {filled && !valid ? (
        <span
          title="This does not look valid"
          style={{
            position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
            display: 'flex', alignItems: 'center', color: '#f59e0b',
          }}
        ><AlertTriangle size={15} /></span>
      ) : (
        <FieldIconButton title={iconTitle} enabled={filled && valid} onClick={onIcon}>
          {icon}
        </FieldIconButton>
      )}
    </div>
  )
}

export function EmailField(props: LinkFieldProps) {
  const v = props.value.trim()
  return (
    <LinkField
      {...props}
      inputType="email" inputMode="email" autoComplete="email"
      valid={looksLikeEmail(v)}
      icon={<Mail size={15} />}
      iconTitle={v ? `Send an email to ${v}` : 'Send an email'}
      onIcon={() => { window.location.href = `mailto:${v}` }}
    />
  )
}

export function PhoneField(props: LinkFieldProps) {
  const v = props.value.trim()
  return (
    <LinkField
      {...props}
      inputType="tel" inputMode="tel" autoComplete="tel"
      valid={looksLikePhone(v)}
      icon={<Phone size={15} />}
      iconTitle={v ? `Call ${v}` : 'Call'}
      onIcon={() => { window.location.href = `tel:${v.replace(/[^\d+]/g, '')}` }}
    />
  )
}

export function WebUrlField(props: LinkFieldProps) {
  const href = webUrlHref(props.value)
  return (
    <LinkField
      {...props}
      inputType="text" inputMode="url" autoComplete="url"
      valid={href != null}
      icon={<ExternalLink size={15} />}
      iconTitle={href ? `Open ${href}` : 'Open link'}
      onIcon={() => { if (href) window.open(href, '_blank', 'noopener,noreferrer') }}
    />
  )
}
